import { MediaItem } from "@/models/MediaItem";
import { MediaPlayerRepeatMode } from "@/models/frontend-only/MediaPlayerRepeatMode";
import { MediaPlayerStatus } from "@/models/frontend-only/MediaPlayerStatus";
import { FEMediaItem, FEMediaUrl } from "@/models/frontend-only/FEMediaItem";

export const useMediaPlayerStore = defineStore("media-player", () => {
  const debug = useDebug();
  const store = useSongStore();
  const toast = useToastStore();
  const { t } = useI18n();
  const song = computed(() => store.song);
  const mediaItems = computed(() => song.value?.media as FEMediaItem[]);

  const spotifyStore = useSpotifyStore();
  const fullSongPlaybackStore = useFullSongPlaybackStore();
  const appleStore = useAppleMusicStore();
  const audioPlayer = useAltAudioPlayback();
  const youtubeStore = useYoutubeStore();
  const nowPlayingId = ref("");
  const error = ref<string | null>(null);
  const previewMode = computed(() => !fullSongPlaybackStore.integration);
  const authenticatedMediaSource = computed(
    () => fullSongPlaybackStore.integration,
  );

  type MediaPlayerTypes = "youtube" | "html-audio" | "apple" | "spotify" | "";

  const mediaPlayer = computed<MediaPlayerTypes>(
    () => (nowPlaying.value && determineMediaPlayer(nowPlaying.value)) || "",
  );

  function determineMediaPlayer(media: FEMediaItem) {
    if (!media) {
      return "";
    } else if (media.type === "video") {
      return "youtube";
    } else if (media.type === "song") {
      if (
        authenticatedMediaSource.value === "spotify" &&
        media.urls.find((u) => u.source === "Spotify") &&
        media.isrc
      ) {
        return "spotify";
      } else if (
        authenticatedMediaSource.value === "apple" &&
        media.urls.find((u) => u.source === "iTunes") &&
        media.applesongid
      ) {
        return "apple";
      } else {
        return "html-audio";
      }
    }
    return "";
  }

  const status = computed(() => {
    if (mediaPlayer.value === "html-audio") {
      return audioPlayer.status;
    } else if (mediaPlayer.value === "spotify") {
      return spotifyStore.status;
    } else if (mediaPlayer.value === "apple") {
      return appleStore.status;
    } else if (mediaPlayer.value === "youtube") {
      return youtubeStore.status;
    }
    return MediaPlayerStatus.Unloaded;
  });

  const progress = computed(() => {
    if (mediaPlayer.value === "html-audio") {
      return audioPlayer.currentTime;
    } else if (mediaPlayer.value === "spotify") {
      return spotifyStore.currentTime;
    } else if (mediaPlayer.value === "apple") {
      return appleStore.currentTime;
    } else if (mediaPlayer.value === "youtube") {
      return youtubeStore.currentTime;
    }
    return 0;
  });

  const duration = computed(() => {
    if (mediaPlayer.value === "html-audio") {
      return audioPlayer.maxTime;
    } else if (mediaPlayer.value === "spotify") {
      return spotifyStore.duration;
    } else if (mediaPlayer.value === "apple") {
      return appleStore.duration;
    } else if (mediaPlayer.value === "youtube") {
      return youtubeStore.duration;
    }
    return 0;
  });

  const nowPlaying = computed(() => {
    if (!mediaItems.value) {
      return null;
    }
    if (!nowPlayingId.value) {
      return null;
    }
    return mediaItems.value.find((i) => i.id === nowPlayingId.value) ?? null;
  });

  function togglePlay() {
    debug.log("toggle play");
    if (mediaPlayer.value === "html-audio") {
      debug.log("toggle play - html-audio");
      toggleAudioPlayback();
    } else {
      switch (status.value) {
        case MediaPlayerStatus.Error:
        case MediaPlayerStatus.Paused:
        case MediaPlayerStatus.Ended:
          debug.log("toggle play - play()");
          play();
          break;

        default:
          debug.log("toggle play - pause()");
          pause();
          break;
      }
    }
  }

  function toggleAudioPlayback() {
    if (status.value !== MediaPlayerStatus.Playing) {
      audioPlayer.play();
    } else {
      audioPlayer.pause();
    }
  }

  function play() {
    if (mediaPlayer.value === "html-audio") {
      audioPlayer.play();
    } else if (mediaPlayer.value === "spotify") {
      spotifyStore.play();
    } else if (mediaPlayer.value === "apple") {
      appleStore.play();
    } else if (mediaPlayer.value === "youtube") {
      youtubeStore.play();
    }
  }

  function pause() {
    audioPlayer.pause();
    if (mediaPlayer.value === "spotify") {
      spotifyStore.pause();
    } else if (mediaPlayer.value === "apple") {
      appleStore.pause();
    } else if (mediaPlayer.value === "youtube") {
      youtubeStore.pause();
    }
  }

  async function goToTime(time: number, final = false) {
    if (mediaPlayer.value === "html-audio") {
      if (final) {
        audioPlayer.goToTime(time);
      }
    } else if (mediaPlayer.value === "spotify") {
      if (final) {
        spotifyStore.seekTo(time);
      }
    } else if (mediaPlayer.value === "apple") {
      if (final) {
        await appleStore.seekTo(time);
        await asyncWait(50); // without this, sometime the seekTo doesn't work.
      }
    } else if (mediaPlayer.value === "youtube") {
      youtubeStore.seekTo(time);
    }
  }

  function playNextSong() {
    if (!mediaItems.value) {
      return;
    }
    let index = mediaItems.value.findIndex((i) => i.id === nowPlayingId.value);
    index++;
    if (index >= mediaItems.value.length) {
      index = 0;
    }
    playMediaItem(mediaItems.value[index].id);
  }

  function playPreviousSong() {
    if (!mediaItems.value) {
      return;
    }
    let index = mediaItems.value.findIndex((i) => i.id === nowPlayingId.value);
    index--;
    if (index < 0) {
      index = mediaItems.value.length - 1;
    }
    playMediaItem(mediaItems.value[index].id);
  }

  async function startMediaPlayback() {
    debug.log("waiting for spotify to complete - ", mediaPlayer.value);
    if (mediaPlayer.value === "spotify") {
      await waitUntilTrue(() => spotifyStore.playerReady);
    }

    debug.log("start media playback", nowPlaying.value, mediaPlayer.value);
    if (!nowPlaying.value) {
      return;
    }

    error.value = null;
    if (mediaPlayer.value === "spotify") {
      const spotifyInfo: FEMediaUrl | null =
        nowPlaying.value.urls.find((url) => url.source === "Spotify") || null;
      debug.log("trying to play song", spotifyInfo);
      if (!spotifyInfo) {
        toast.errorToast(t("SV.MES_SpotifyConnectionError"));
        throw new Error("Item does not contain isrc");
      }
      spotifyStore.playSong(spotifyInfo.id);
    } else if (mediaPlayer.value === "apple") {
      debug.log("playing apple item", nowPlaying.value.applesongid);
      if (!nowPlaying.value.applesongid) {
        console.error("apple song id missing");
        throw new Error("apple song id missing");
      }
      appleStore.playSong(nowPlaying.value.applesongid).catch((err) => {
        nowPlayingId.value = "";
        throw err;
      });
    } else if (mediaPlayer.value === "html-audio") {
      if (!nowPlaying.value.previewUrl) {
        toast.errorToast(t("SV.MES_SongCannotPlayError"));
        throw new Error("Item does not contain a url");
      }
      audioPlayer.loadUrl(nowPlaying.value.previewUrl);
    } else if (mediaPlayer.value === "youtube") {
      audioPlayer.pause();
    } else {
      if (!nowPlaying.value.previewUrl) {
        return "";
      }

      audioPlayer.loadUrl(nowPlaying.value.previewUrl);
      audioPlayer.play();
    }
  }

  async function playMediaItem(mediaId: string, dontStart = false) {
    if (
      status.value === MediaPlayerStatus.Loading ||
      status.value === MediaPlayerStatus.Playing
    ) {
      pause();
    }
    nowPlayingId.value = mediaId;
    if (!dontStart) {
      startMediaPlayback();
    }
  }

  const repeatMode = ref<MediaPlayerRepeatMode>(MediaPlayerRepeatMode.NoRepeat);

  function toggleRepeatMode() {
    switch (repeatMode.value) {
      case MediaPlayerRepeatMode.NoRepeat:
        repeatMode.value = MediaPlayerRepeatMode.RepeatPlaylist;
        break;
      case MediaPlayerRepeatMode.RepeatPlaylist:
        repeatMode.value = MediaPlayerRepeatMode.RepeatSong;
        break;
      case MediaPlayerRepeatMode.RepeatSong:
        repeatMode.value = MediaPlayerRepeatMode.NoRepeat;
        break;
    }
  }

  function reset() {
    pause();
    nowPlayingId.value = "";
  }

  function getYoutubeUrl(m: MediaItem | FEMediaItem) {
    return m.urls.find((url) => url.source === "youtube")?.url || null;
  }

  emitter.on("youtubeVideoEnded", () => (nowPlayingId.value = ""));

  return {
    song,
    mediaItems,
    mediaPlayer,
    nowPlaying,
    nowPlayingId,
    error,
    togglePlay,
    repeatMode,
    toggleRepeatMode,
    pause,
    play,
    reset,
    goToTime,
    playMediaItem,
    playNextSong,
    playPreviousSong,
    status,
    previewMode,
    progress,
    authenticatedMediaSource,
    duration,
    getYoutubeUrl,
    determineMediaPlayer,
  };
});
